import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { cn } from '@/lib/utils';
import { Link, router } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { useCallback, useState } from 'react';
import CategoryHero from './journal/CategoryHero';
import ClosingInterstitial from './journal/ClosingInterstitial';
import { articleUrl, itemVariants, sectionColors, sectionVariants } from './journal/constants';
import JournalNav from './journal/JournalNav';
import type { JournalCategoryPageProps, JournalGuide } from './journal/types';

/** Compact vertical card matching SectionCompact pattern — image, kicker, title, excerpt */
function CompactCard({ guide, colors }: { guide: JournalGuide; colors: ReturnType<typeof sectionColors> }) {
    const tag = guide.country_region ?? guide.tags?.[0]?.name;

    return (
        <Link href={articleUrl(guide.slug)} className="group">
            {guide.main_image && (
                <div className="relative aspect-[3/2] overflow-hidden">
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            <div className="pt-4">
                {tag && <span className={cn('lux-caps lux-caps-sm', colors.kicker)}>{tag}</span>}
                <h4 className={cn('lux-didot lux-didot-md mt-1 line-clamp-2 transition-colors', colors.cardTitle, colors.cardHover)}>
                    {guide.title}
                </h4>
                {guide.excerpt && <p className={cn('lux-body lux-body-sm mt-2 line-clamp-2', colors.body)}>{guide.excerpt}</p>}
            </div>
        </Link>
    );
}

export default function JournalCategory({ currentSection, sections, hero, featured, guides, hasMore, page }: JournalCategoryPageProps) {
    const shouldReduceMotion = useReducedMotion();
    const archiveColors = sectionColors('ivory');
    const [loading, setLoading] = useState(false);

    const loadMore = useCallback(() => {
        if (loading || !hasMore) return;
        setLoading(true);
        router.reload({
            data: { page: page + 1 },
            only: ['guides', 'hasMore', 'page'],
            onFinish: () => setLoading(false),
        });
    }, [loading, hasMore, page]);

    return (
        <PublicLayout headerConfig={{ position: 'fixed' }}>
            <MetaHead
                title={`${currentSection.name} | Journal | LuxRally Travel`}
                description={`${currentSection.kicker ?? currentSection.name} — articles from the LuxRally Journal.`}
            />

            <div className="bg-[#0e2727]">
                <JournalNav sections={sections} activeTag={currentSection.tag} />

                <CategoryHero currentSection={currentSection} hero={hero} featured={featured} />

                {/* Archive rows — ivory background for contrast */}
                {guides.length > 0 && (
                    <motion.section
                        className={cn('lux-space-px', archiveColors.bg)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-80px' }}
                        variants={shouldReduceMotion ? undefined : sectionVariants}
                    >
                        <div className="mx-auto max-w-[1400px] pt-14 pb-16 lg:pt-20 lg:pb-24">
                            <motion.div className="mb-8 lg:mb-10" variants={shouldReduceMotion ? undefined : itemVariants}>
                                <span className={cn('lux-caps lux-caps-sm mb-1.5 block', archiveColors.kicker)}>From the Archives</span>
                                <div className="mt-4 flex items-center gap-0">
                                    <div className={cn('h-0.5 w-10', archiveColors.decorativeRule)} />
                                    <div className={cn('h-px flex-1', archiveColors.decorativeRule, 'opacity-50')} />
                                </div>
                            </motion.div>

                            <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-12">
                                {guides.map((guide) => (
                                    <motion.div key={guide.id} variants={shouldReduceMotion ? undefined : itemVariants}>
                                        <CompactCard guide={guide} colors={archiveColors} />
                                    </motion.div>
                                ))}
                            </div>

                            {hasMore && (
                                <motion.div className="mt-16 flex justify-center" variants={shouldReduceMotion ? undefined : itemVariants}>
                                    <button
                                        type="button"
                                        onClick={loadMore}
                                        disabled={loading}
                                        className="lux-caps lux-caps-sm inline-flex h-12 items-center justify-center rounded-sm border border-[#0e2727]/25 px-10 text-[#0e2727] transition-all duration-500 hover:border-[#0e2727]/50 hover:bg-[#0e2727]/5 disabled:opacity-50"
                                    >
                                        {loading ? 'Loading\u2026' : 'Load More'}
                                    </button>
                                </motion.div>
                            )}
                        </div>
                    </motion.section>
                )}

                <ClosingInterstitial />
            </div>
        </PublicLayout>
    );
}
